import React, { useMemo } from 'react';
import { TrendingUp, Calendar, PackageOpen } from 'lucide-react';

const formatPrice = (val) => {
    if (val == null || String(val).toUpperCase() === 'N/A') return 'N/A';
    const num = parseFloat(val);
    if (Number.isNaN(num)) return 'N/A';
    return num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const ProcurementForesight = ({ procurementForesight }) => {
    const items = procurementForesight?.items ?? [];
    const totalBudget = useMemo(() => {
        return items.reduce((sum, it) => {
            const p = it.estimatedPrice;
            if (p == null || String(p).toUpperCase() === 'N/A') return sum;
            const num = parseFloat(p);
            return Number.isNaN(num) ? sum : sum + num;
        }, 0);
    }, [items]);

    const hasItems = items.length > 0;

    return (
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 flex flex-col h-full">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <div className="flex items-center gap-2">
                    <TrendingUp className="text-gray-900" size={20} />
                    <h2 className="text-xl font-bold text-gray-900">Procurement Foresight</h2>
                </div>
            </div>

            {hasItems && (
                <div className="bg-[#EEF2FF] border border-[#C7D2FE] rounded-xl p-5 mb-8 flex items-center justify-between">
                    <span className="text-[#1E3A8A] font-bold text-lg">Total Estimated Budget</span>
                    <span className="text-[#1E3A8A] font-bold text-2xl">₹ {formatPrice(totalBudget)}</span>
                </div>
            )}

            {hasItems ? (
                <>
                    {/* Table Header */}
                    <div className="grid grid-cols-12 gap-4 text-xs font-semibold text-gray-500 border-b border-gray-200 pb-2 mb-4">
                        <div className="col-span-2">Material</div>
                        <div className="col-span-2 text-center">Required</div>
                        <div className="col-span-2 text-center">Available</div>
                        <div className="col-span-2 text-center">To Purchase</div>
                        <div className="col-span-2 text-center">Timeline (Days)</div>
                        <div className="col-span-2 text-right">Est. Price</div>
                    </div>

                    {/* Items */}
                    <div className="space-y-4">
                        {items.map((item) => (
                            <div
                                key={item.itemId}
                                className="bg-gray-50 rounded-xl p-4 grid grid-cols-12 gap-4 items-center hover:bg-gray-100 transition-all border border-transparent hover:border-gray-200"
                            >
                                <div className="col-span-2">
                                    <div className="font-bold text-gray-900 text-base leading-tight">{item.material}</div>
                                    {item.unit && <div className="text-xs text-gray-500">{item.unit}</div>}
                                </div>
                                <div className="col-span-2 text-center">
                                    <div className="text-lg font-bold text-gray-900">{item.required ?? 0}</div>
                                    {item.unit && <div className="text-xs text-gray-500">{item.unit}</div>}
                                </div>
                                <div className="col-span-2 text-center">
                                    <div className="text-lg font-bold text-gray-900">{item.available ?? 0}</div>
                                    {item.unit && <div className="text-xs text-gray-500">{item.unit}</div>}
                                </div>
                                <div className="col-span-2 text-center">
                                    <div className="text-lg font-bold text-gray-900">{item.toPurchase ?? 0}</div>
                                    {item.unit && <div className="text-xs text-gray-500">{item.unit}</div>}
                                </div>
                                <div className="col-span-2 flex flex-col items-center">
                                    <div className="flex items-center gap-1.5 text-gray-800 font-medium text-sm">
                                        <Calendar size={14} />
                                        {item.timeline != null ? `${item.timeline} day${item.timeline !== 1 ? 's' : ''}` : '—'}
                                    </div>
                                </div>
                                <div className="col-span-2 text-right font-bold text-gray-900 text-base">
                                    {String(item.estimatedPrice || '').toUpperCase() === 'N/A'
                                        ? 'N/A'
                                        : `₹ ${formatPrice(item.estimatedPrice)}`}
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <div className="flex flex-col items-center justify-center py-12 px-6 bg-gray-50 rounded-2xl border border-gray-100 border-dashed">
                    <PackageOpen className="w-14 h-14 text-gray-300 mb-3" strokeWidth={1.25} />
                    <p className="text-base font-semibold text-gray-500 mb-1">No procurement data</p>
                    <p className="text-sm text-gray-400 text-center">No procurement foresight for the selected month.</p>
                </div>
            )}
        </div>
    );
};

export default ProcurementForesight;
